import { Button, Dropdown, Layout, Space, Typography, type MenuProps } from 'antd';
import React, { useState } from 'react';

import { DevSettingsModal } from '@/components/dev/DevSettingsModal';
import { config } from '@/config';
import { LEVEL_LABELS, ROLE_LABELS } from '@/hooks/devRoleLabels';
import { useDevRoleOverride } from '@/hooks/useDevRoleOverride';
import { useThemeMode, type ThemePreference } from '@/hooks/useThemeMode';
import { SettingsIcon, SignOutIcon, ThemeIcon, UserIcon, VerticalEllipsisIcon } from '@/icons';

const IS_DEV = config.environment === 'development';

import { AppBreadcrumb, type BreadcrumbItem } from './AppBreadcrumb';

/** Props for the `TopBar` application header. */
export interface TopBarProps {
  title: string;
  subtitle?: string;
  /** When provided, replaces title/subtitle with a URL-based breadcrumb trail. */
  breadcrumbs?: BreadcrumbItem[];
  /** Required when breadcrumbs is provided. */
  onNavigate?: (path: string) => void;
  userName: string;
  onOpenProfile: () => void;
  onSignOut: () => void;
  showAddAsset?: boolean;
  onOpenAddAsset?: () => void;
}

const { Header } = Layout;
const { Text, Title } = Typography;

/** Application header bar with page title/breadcrumbs, add-asset button, and user dropdown menu. */
export const TopBar: React.FC<TopBarProps> = ({
  title,
  subtitle,
  breadcrumbs,
  onNavigate,
  userName,
  onOpenProfile,
  onSignOut,
  showAddAsset = false,
  onOpenAddAsset,
}) => {
  const { mode: themeMode, setMode: setThemeMode } = useThemeMode();
  const { roleState, setRoleState, clearRoleState } = useDevRoleOverride();
  const [devSettingsOpen, setDevSettingsOpen] = useState(false);

  const themeOptions: ReadonlyArray<{ key: ThemePreference; label: string }> = [
    { key: 'light', label: 'Light' },
    { key: 'dark', label: 'Dark' },
    { key: 'system', label: 'System (auto)' },
  ];

  const menuItems: MenuProps['items'] = [
    {
      key: 'theme',
      label: 'Theme',
      icon: <ThemeIcon />,
      children: themeOptions.map(({ key, label }) => ({
        key: `theme:${key}`,
        label: themeMode === key ? `${label} ✓` : label,
      })),
    },
    ...(IS_DEV
      ? [
          {
            key: 'dev-settings',
            label: (() => {
              if (!roleState) {
                return 'Dev Settings';
              }
              // Abbreviated overrides for the compact menu label only.
              // Full labels live in devRoleLabels.ts; only entries that differ are listed here.
              const levelShort: Partial<typeof LEVEL_LABELS> = {
                lead: 'Sr. Consultant',
                vp: 'VP',
              };
              const roleShort: Partial<typeof ROLE_LABELS> = {
                pm: 'Product Mgmt',
                project_management: 'Project Mgmt',
                fullstack: 'Technology',
                de: 'Data Eng',
                ai: 'AI Eng',
              };
              const levelLabel = levelShort[roleState.level] ?? LEVEL_LABELS[roleState.level];
              const roleLabel = roleShort[roleState.role] ?? ROLE_LABELS[roleState.role];
              return `Dev Settings (${levelLabel} · ${roleLabel})`;
            })(),
            icon: <SettingsIcon />,
          },
        ]
      : []),
    {
      type: 'divider' as const,
    },
    {
      key: 'sign-out',
      label: 'Sign Out',
      icon: <SignOutIcon />,
      danger: true,
    },
  ];

  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key.startsWith('theme:')) {
      const next = key.slice('theme:'.length) as ThemePreference;
      setThemeMode(next);
      return;
    }
    if (key === 'dev-settings') {
      setDevSettingsOpen(true);
      return;
    }
    if (key === 'sign-out') {
      onSignOut();
    }
  };

  return (
    <>
      {IS_DEV && (
        <DevSettingsModal
          open={devSettingsOpen}
          currentRoleState={roleState}
          onApply={(state) => {
            setRoleState(state);
            setDevSettingsOpen(false);
          }}
          onClear={() => {
            clearRoleState();
            setDevSettingsOpen(false);
          }}
          onCancel={() => setDevSettingsOpen(false)}
        />
      )}
      <Header className="app-shell__header">
        <div className="app-shell__header-left">
          {breadcrumbs && breadcrumbs.length > 0 && onNavigate ? (
            <AppBreadcrumb items={breadcrumbs} onNavigate={onNavigate} />
          ) : (
            <>
              <Title level={4} className="app-shell__title">
                {title}
              </Title>
              {subtitle ? <Text className="app-shell__subtitle">{subtitle}</Text> : null}
            </>
          )}
        </div>

        <Space size="middle">
          <div id="app-shell-header-actions" className="app-shell__header-actions" />

          {showAddAsset ? (
            <Button
              onClick={onOpenAddAsset}
              className="app-shell__add-asset-trigger"
              aria-label="Open add asset modal"
              disabled={!onOpenAddAsset}
            >
              Add asset
            </Button>
          ) : null}

          <Dropdown.Button
            menu={{ items: menuItems, onClick: onMenuClick }}
            onClick={onOpenProfile}
            icon={<VerticalEllipsisIcon />}
          >
            <Space size={8}>
              <UserIcon />
              <Text>{userName}</Text>
            </Space>
          </Dropdown.Button>
        </Space>
      </Header>
    </>
  );
};

export default TopBar;
